/* eslint-disable no-constant-condition */
import { all, call, fork, put, takeEvery, take, cancelled, cancel, select } from 'redux-saga/effects';
import { eventChannel } from 'redux-saga';
import i18n from 'i18next';

// Firestore imports
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/firestore';
import { removeDevicesFromSubscriptionMap, RealtimePayload, receiveDeviceRealtimeData } from 'actions/Realtime';
import { SUBSCRIBE_DEVICE_DATA, UNSUBSCRIBE_DEVICE_DATA } from 'constants/ActionTypes';
import { CustomAction } from 'model/state';
import { showError } from 'actions';
import { issueToken } from 'api/Firebase';
import config from 'config/config';
import { selectUserUnitSystem } from 'selectors';
import { toRealtimeDeviceData } from 'model/firestore';

const settings = { ...config.firebase };

function firebaseAuth(token: string) {
  return firebase.auth().signInWithCustomToken(token);
}

function isConnectedFirestore() {
  try {
    firebase.app();
    return true;
  } catch (err) {
    return false;
  }
}

function* connectFirestore() {
  if (!isConnectedFirestore()) {
    firebase.initializeApp(settings);
    try {
      const data = yield call(issueToken);
      yield call(firebaseAuth, data.token);
    } catch (error) {
      console.log(error);
    }
  }
}

function createSubscriptionChannel(firestoreQuery: any) {
  // `eventChannel` takes a subscriber function
  // the subscriber function takes an `emit` argument to put messages onto the channel
  return eventChannel((emit) => {

    const queryHandler = (event: any) => {
      // puts event payload into the channel
      // this allows a Saga to take this payload from the returned channel
      emit(event);
    };

    // setup the subscription
    const unsubscribeFn = firestoreQuery.onSnapshot(queryHandler);

    // the subscriber must return an unsubscribe function
    // this will be invoked when the saga calls `channel.close` method
    const unsubscribe = () => {
      unsubscribeFn();
    };

    return unsubscribe;
  });
}

function* subscribeDevice(macAddress: string) {
  yield call(connectFirestore);

  const db = firebase.firestore();
  const device = (macAddress: string) => db.doc(`devices/${macAddress}`);
  const query = device(macAddress);
  // creates the channel
  const firestoreChannel = yield call(createSubscriptionChannel, query);

  try {
    while (true) {
      // An error from socketChannel will cause the saga jump to the catch block
      const snapshot = yield take(firestoreChannel);
      const data = snapshot.data();
      const userUnitSystem = yield select(selectUserUnitSystem());
      yield put(receiveDeviceRealtimeData(toRealtimeDeviceData(data, userUnitSystem)));
    }
  } finally {
    if (yield cancelled()) {
      firestoreChannel.close();
    }
  }
}

function* subscribeDevicesWorker(action: CustomAction<RealtimePayload>) {
  const macAddresses = action.payload?.macAddresses;
  try {
    if (!macAddresses) {
      throw Error('Invalid Parameters');
    }

    const tasks = [];
    for (let i = 0; i < macAddresses.length; i++) {
      const task = yield fork(subscribeDevice, macAddresses[i]);
      tasks.push(task);
    }
  
    // Wait here until an UNSUBSCRIBE_DEVICE_DATA action is dispatched.
    yield take(UNSUBSCRIBE_DEVICE_DATA);
    for (let i = 0; i < tasks.length; i++) {
      yield cancel(tasks[i]);
    }
    yield put(removeDevicesFromSubscriptionMap(macAddresses));
  } catch (error) {
    yield put(showError(i18n.t('connection_error')));
  }
}

export function* subscribeDevicesSaga() {
  yield takeEvery(SUBSCRIBE_DEVICE_DATA, subscribeDevicesWorker);
}

export default function* rootSaga() {
  yield all([
    fork(subscribeDevicesSaga),
  ]);
}
